import { t, useLocale } from '../lib/i18n.js';
import { useEffect, useMemo, useState } from 'react';
import {
  LETTERS,
  PACK_SETS,
  maskOf,
  pickHintIndex,
  pointsForCorrect,
  renderPackDrawing,
  type Phase,
  type RoundPublic,
  type StageRelease,
  type Stroke,
} from '@listen-draw/shared';
import { Canvas } from './Canvas.js';
import { Choices } from './Choices.js';

/**
 * トップページ用のライブデモ。
 * 本物の Canvas / Choices コンポーネントに、同梱セットの線画と時刻をそのまま流し込んで
 * 「読み上げ → 描画 → A〜D を押す → 描画完成 → 答え合わせ」を約 18 秒でループ再生する。
 * 動画ではないので軽く、ダークモードでも本番と同じ見た目になる。
 */

const SET = PACK_SETS[0]!;
/** letterIndex -> sentenceIndex。B を正解（机の下のかばん）にする */
const ORDER = [2, 1, 0, 3];
const CORRECT_SI = 1;
const CORRECT_LI = ORDER.indexOf(CORRECT_SI);

const LISTEN_STEP = 950;
const DRAW_START = LISTEN_STEP * 4 + 500;
const DRAW_DUR = 9000;
const PICK_PCT = 58;
const PICK_AT = DRAW_START + Math.round((DRAW_DUR * PICK_PCT) / 100);
const COMPLETE_AT = DRAW_START + DRAW_DUR;
const REVEAL_AT = COMPLETE_AT + 600;
const CYCLE_MS = REVEAL_AT + 4200;
const POINTS = pointsForCorrect(PICK_PCT);

type Scene = 'listen' | 'drawing' | 'picked' | 'reveal';

function sceneAt(t: number): { scene: Scene; playing: number } {
  if (t < DRAW_START) return { scene: 'listen', playing: Math.min(3, Math.floor(t / LISTEN_STEP)) };
  if (t < PICK_AT) return { scene: 'drawing', playing: -1 };
  if (t < REVEAL_AT) return { scene: 'picked', playing: -1 };
  return { scene: 'reveal', playing: -1 };
}

/** 段階ごとの時間配分（サーバーの buildStageTimeline の簡略版） */
function buildStages(strokes: Stroke[], durationMs: number): StageRelease[] {
  const share: Record<number, number> = { 1: 0.45, 2: 0.2, 3: 0.2, 4: 0.15 };
  const indices = [...new Set(strokes.map((s) => s.stage))].sort((a, b) => a - b);
  const total = indices.reduce((a, i) => a + (share[i] ?? 0.1), 0);
  let cursor = 0;
  return indices.map((index, k) => {
    const ms = Math.round((durationMs * (share[index] ?? 0.1)) / total);
    const startMs = cursor;
    const endMs = k === indices.length - 1 ? durationMs : cursor + ms;
    cursor = endMs;
    return { index, strokes: strokes.filter((s) => s.stage === index), startMs, endMs };
  });
}

function seeded(seed: number): () => number {
  let x = seed;
  return () => {
    x = (x * 9301 + 49297) % 233280;
    return x / 233280;
  };
}

const STAGES = buildStages(renderPackDrawing(SET, CORRECT_SI).strokes, DRAW_DUR);
const TEXTS = ORDER.map((si) => SET.sentences[si]!.text);
const rng = seeded(11);
const MASKS = TEXTS.map((t) => maskOf(t, pickHintIndex(t, rng)));
const CORRECT = SET.sentences[CORRECT_SI]!;
const KEY_WORDS = SET.axes.map((a) => CORRECT.features[a.name] ?? '').filter(Boolean);

const STATUS = (): Record<Scene, { label: string; kind: '' | 'live' | 'ok' | 'warn' }> => ({
  listen: { label: t('1 · Listen to four sentences', '読み上げ中 — 文字は伏せ字'), kind: 'warn' },
  drawing: {
    label: t('2 · Watch the sketch appear', '描画中 — わかった瞬間に A〜D を押す'),
    kind: 'live',
  },
  picked: {
    label: t(
      '3 · Answer locked. Wait for the reveal.',
      '回答済み — 描画は止まらない。最速の正解者が勝ち',
    ),
    kind: 'live',
  },
  reveal: {
    label: t('4 · See the answer and key words', '答え合わせ — 4 文と決め手の語'),
    kind: 'ok',
  },
});

export function HeroDemo() {
  useLocale();
  const reduced = useMemo(
    () =>
      typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );
  const [cycleStart, setCycleStart] = useState(() => Date.now());
  const [key, setKey] = useState<string>(reduced ? 'reveal:-1' : 'listen:0');

  useEffect(() => {
    if (reduced) return;
    const id = window.setInterval(() => {
      const t = Date.now() - cycleStart;
      if (t >= CYCLE_MS) {
        setCycleStart(Date.now());
        setKey('listen:0');
        return;
      }
      const s = sceneAt(t);
      const k = `${s.scene}:${s.playing}`;
      setKey((prev) => (prev === k ? prev : k));
    }, 80);
    return () => window.clearInterval(id);
  }, [cycleStart, reduced]);

  const [scene, playingStr] = key.split(':') as [Scene, string];
  const playing = Number(playingStr);
  const phase: Phase = scene === 'listen' ? 'listening' : scene === 'reveal' ? 'reveal' : 'drawing';

  const round = useMemo<RoundPublic>(() => {
    const r: RoundPublic = {
      index: 1,
      masks: MASKS,
      audio: { replays: 0, halved: false, done: scene !== 'listen' },
      drawingReady: true,
      drawingSource: 'pack',
      answeredCount: scene === 'listen' || scene === 'drawing' ? 0 : 1,
    };
    if (scene !== 'listen') {
      r.drawing = {
        totalStages: STAGES.length,
        durationMs: DRAW_DUR,
        startAt: cycleStart + DRAW_START,
        pausedAt: null,
        pausedTotalMs: 0,
        stages: STAGES,
      };
    }
    if (scene === 'reveal') {
      r.reveal = {
        texts: TEXTS,
        correctLetterIndex: CORRECT_LI,
        axes: SET.axes,
        keyWords: KEY_WORDS,
        scene: SET.scene,
        outcomes: [],
        distribution: [0, 0, 0, 0],
        winnerPlayerId: null,
      };
    }
    return r;
  }, [scene, cycleStart]);

  const status = STATUS()[scene];
  const picked = scene === 'picked' || scene === 'reveal';

  return (
    <div
      className="demo"
      aria-label={t(
        'A preview of listening, watching the sketch, choosing A–D and revealing the answer',
        'ゲームの流れのデモ: 英文の読み上げ、線画の描画、A〜D を押して回答、答え合わせ',
      )}
    >
      <div className="demo__status">
        <span className={`pill${status.kind ? ` pill--${status.kind}` : ''}`}>{status.label}</span>
      </div>
      <div className="demo__row" aria-hidden="true">
        <div className="demo__canvas">
          <Canvas round={round} phase={phase} />
        </div>
        <div className={`demo-phone${scene === 'picked' ? ' demo-phone--picked' : ''}`}>
          <div className="demo-phone__head">
            <span>{t('Player 41', 'ネコ41')}</span>
            <span className="num">
              {scene === 'reveal' ? POINTS : 0} {t(' pts', '点')}
            </span>
          </div>
          {scene === 'listen' && (
            <div className="demo-phone__note">{t('Listening…', '耳で聞く…')}</div>
          )}
          {scene !== 'listen' && (
            <>
              <div className="demo-phone__note">
                {scene === 'drawing' ? (
                  <>
                    {t('Choose now ', 'わかったら押す ')}
                    <b>
                      {pointsForCorrect(40)} {t(' pts', '点')}
                    </b>
                  </>
                ) : (
                  <>
                    {t('Answer locked: ', '回答済み: ')}
                    <b>{LETTERS[CORRECT_LI]}</b>
                    {t(' (at ', '（完成率 ')}
                    {PICK_PCT}%）
                  </>
                )}
              </div>
              <div className="answer-grid answer-grid--demo">
                {LETTERS.map((L, i) => (
                  <span
                    key={L}
                    className={picked && i === CORRECT_LI ? 'is-picked' : picked ? 'is-dim' : ''}
                  >
                    {L}
                  </span>
                ))}
              </div>
            </>
          )}
          {scene === 'reveal' && (
            <div className="demo-result">
              {t('Correct!', '正解！')}
              <b>
                +{POINTS} {t(' pts', '点')}
              </b>
              <small>
                {t('At ', '完成率 ')}
                {PICK_PCT}
                {t('% · fastest correct', '% で回答 · 最速正解')}
              </small>
            </div>
          )}
        </div>
      </div>
      <div className="demo__choices" aria-hidden="true">
        <Choices
          round={round}
          playing={scene === 'listen' ? playing : null}
          hintUpTo={scene === 'listen' ? playing : undefined}
          picked={picked && scene !== 'reveal' ? CORRECT_LI : null}
          myLetter={scene === 'reveal' ? CORRECT_LI : null}
          compact
        />
      </div>
    </div>
  );
}
